import React, { useState } from "react";
import { useParams } from "react-router";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import styled from "styled-components";

import CombinedTrendCard from "./CombinedTrendCard";
import { useTrendsData } from "./hooks/useTrendsData";
import { sumRecommendations, sumRecommended } from "./utils";

const COLOUR_IMPLEMENTED = "#1976d2";
const COLOUR_RECOMMENDED = "#2e7d32";

const StyledToggle = styled(ToggleButtonGroup)`
    && .MuiToggleButton-root {
        padding: 4px 14px;
        font-size: 0.75rem;
        text-transform: none;
        border-color: #e5e7eb;
        color: #6b7280;
        border-radius: 8px !important;
    }
    && .MuiToggleButton-root.Mui-selected {
        background-color: #eff6ff;
        color: #1976d2;
        font-weight: 600;
    }
    && .MuiToggleButtonGroup-grouped:not(:first-child) {
        margin-left: 6px;
        border-left: 1px solid #e5e7eb !important;
        border-radius: 8px !important;
    }
    && .MuiToggleButtonGroup-grouped:not(:last-child) {
        border-radius: 8px !important;
    }
`;

export default function RecommendationsSection({ startDate, endDate }) {
    const { workspaceNumber } = useParams();
    const { series } = useTrendsData({ workspaceNumber, startDate, endDate });

    const [active, setActive] = useState("totals");

    const totalsImplemented = series((s) => sumRecommendations(s.recommendationStats || {}));
    const totalsRecommended = series((s) => sumRecommended(s.recommendationStats || {}));

    const titleImpl = series((s) => s.recommendationStats?.implementedTitleCount ?? 0);
    const titleRec  = series((s) => s.recommendationStats?.recommendedTitleCount ?? 0);
    const metaImpl  = series((s) => s.recommendationStats?.implementedMetaDescriptionsCount ?? 0);
    const metaRec   = series((s) => s.recommendationStats?.recommendedMetaDescriptionsCount ?? 0);
    const h1Impl    = series((s) => s.recommendationStats?.implementedH1sCount ?? 0);
    const h1Rec     = series((s) => s.recommendationStats?.recommendedH1sCount ?? 0);
    const redImpl   = series((s) => s.recommendationStats?.implementedDestinationUriCount ?? 0);
    const redRec    = series((s) => s.recommendationStats?.recommendedDestinationUriCount ?? 0);

    const makeImpl = (impl, rec) => [
        { label: "Implemented", series: impl, stroke: COLOUR_IMPLEMENTED },
        { label: "Recommended", series: rec,  stroke: COLOUR_RECOMMENDED },
    ];

    let chartSeries;
    switch (active) {
        case "title":    chartSeries = makeImpl(titleImpl, titleRec); break;
        case "meta":     chartSeries = makeImpl(metaImpl,  metaRec);  break;
        case "h1":       chartSeries = makeImpl(h1Impl,    h1Rec);    break;
        case "redirect": chartSeries = makeImpl(redImpl,   redRec);   break;
        default:         chartSeries = makeImpl(totalsImplemented, totalsRecommended);
    }

    const titleText =
        active === "totals"
            ? "Recommendations — totals"
            : `Recommendations — ${active === "h1" ? "H1" : active.charAt(0).toUpperCase() + active.slice(1)}`;

    const tooltipText =
        "Green = recommended, Blue = implemented. Compares across the selected period.";

    return (
        <CombinedTrendCard
            title={titleText}
            tooltip={tooltipText}
            startDate={startDate}
            endDate={endDate}
            series={chartSeries}
        >
            <StyledToggle
                value={active}
                exclusive
                onChange={(_, v) => v && setActive(v)}
                size="small"
            >
                <ToggleButton value="totals">All</ToggleButton>
                <ToggleButton value="title">Title</ToggleButton>
                <ToggleButton value="meta">Meta</ToggleButton>
                <ToggleButton value="h1">H1</ToggleButton>
                <ToggleButton value="redirect">Redirect</ToggleButton>
            </StyledToggle>
        </CombinedTrendCard>
    );
}
